import React from 'react'
import styled from 'styled-components'
import { OverflowCell } from '../../../../components/OverflowCell'
import { StandardColumn } from '../../../../components/StandardPage'
import openSignedAttachment from '../../../../utils/openSignedAttachment'

export interface PriceItem {
  id: number
  price: string | number
  unitOfMeasurement?: { id: number; name: string }
  UnitOfMeasurement?: { id: number; name: string }
  unit?: string
}

export interface Resource {
  id: number
  name: string
  code?: string
  codeId?: number | null
  category: { id: number; name: string } | null
  type: { id: number; name: string } | null
  prices?: PriceItem[]
  quantities?: any[]
  description: string
  totalWeight?: number
  lengthInMm?: number
  unitQuantity?: number
  stockUnit?: string
  minQuantity?: number | null
  maxQuantity?: number | null
  markup?: number | string | null
  dimension1?: number | null
  dimension2?: number | null
  material?: string | null
  format?: string | null
  finishing?: string | null
  fabrication?: string | null
  ncm?: string | null
  shelf?: string | null
  stockLocation?: string | null
  stockStreet?: string | null
  suppliers: { id: number; companyName: string; cnpj: string }[]
  colors?: Array<{ id: number; name: string; hex?: string; pantone?: { code?: string; hex?: string } }>
  isActivated?: boolean
  externalInvoiceUrl?: string | null
}

export const resourceScreenKey = (isFashion: boolean) =>
  isFashion ? 'resource_fashion' : 'resource_steel'

interface ResourceColumnDef {
  id: string
  label: string
  visible: boolean
  parentId?: string
}

interface ResourceQuantityColumn {
  key: string
  label: string
  names: string[]
}

interface ResourcePriceColumn {
  key: string
  label: string
  names: string[]
}

const resourceQuantityColumns: ResourceQuantityColumn[] = [
  { key: 'un', label: 'Quantidade (un)', names: ['UN', 'UNIDADE', 'PC', 'PÇ', 'PECA', 'PEÇA'] },
  { key: 'kg', label: 'Peso total (kg)', names: ['KG', 'QUILOGRAMA', 'KILO', 'QUILO'] },
  { key: 'mm', label: 'Comprimento (mm)', names: ['MM', 'MILIMETRO', 'MILÍMETRO'] },
  { key: 'm2', label: 'Quantidade (m²)', names: ['M²', 'M2', 'METRO QUADRADO'] },
  { key: 'mlinear', label: 'Comprimento (m linear)', names: ['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'] },
  { key: 'percent', label: 'Quantidade (%)', names: ['%', 'PORCENTAGEM', 'PERCENT', 'PERCENTUAL'] },
  { key: 'liters', label: 'Quantidade (litros)', names: ['LITROS', 'LITRO', 'L', 'LT', 'LTS'] },
]

const resourcePriceColumns: ResourcePriceColumn[] = [
  { key: 'kg', label: 'Custo (kg)', names: ['KG', 'QUILOGRAMA', 'KILO', 'QUILO'] },
  { key: 'mm', label: 'Custo (mm)', names: ['MM', 'MILIMETRO', 'MILÍMETRO'] },
  { key: 'un', label: 'Custo (un)', names: ['UN', 'UNIDADE', 'PC', 'PÇ', 'PECA', 'PEÇA'] },
  { key: 'm2', label: 'Custo (m²)', names: ['M²', 'M2', 'METRO QUADRADO'] },
  { key: 'mlinear', label: 'Custo (m linear)', names: ['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'] },
  { key: 'percent', label: 'Custo (%)', names: ['%', 'PORCENTAGEM', 'PERCENT', 'PERCENTUAL'] },
  { key: 'liters', label: 'Custo (litros)', names: ['LITROS', 'LITRO', 'L', 'LT', 'LTS'] },
]

const withUnitSubColumns = (defs: ResourceColumnDef[]): ResourceColumnDef[] =>
  defs.flatMap((def) => {
    if (def.id === 'quantities') {
      return [
        def,
        ...resourceQuantityColumns.map((unit) => ({
          id: `quantities_${unit.key}`,
          label: unit.label,
          visible: false,
          parentId: 'quantities',
        })),
      ]
    }
    if (def.id === 'prices') {
      return [
        def,
        ...resourcePriceColumns.map((unit) => ({
          id: `prices_${unit.key}`,
          label: unit.label,
          visible: false,
          parentId: 'prices',
        })),
      ]
    }
    return [def]
  })

const ColorSwatchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ColorSwatchCircle = styled.span<{ $hex?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => {
    const hex = props.$hex
    if (!hex) return '#bdbdbd'
    return hex.startsWith('#') ? hex : `#${hex}`
  }};
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: inline-block;
  flex-shrink: 0;
`

const ColorListCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CellLink = styled.a`
  text-decoration: none;
  text-align: center;
  color: #0090d3;
  font-weight: bold;
`

const InvoiceCell: React.FC<{ item: Resource }> = ({ item }) => {
  const url = item.externalInvoiceUrl || (item as any).invoiceUrl
  if (url) {
    return (
      <CellLink
        href={url}
        onClick={(e) => {
          e.preventDefault()
          openSignedAttachment(url)
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        {(item as any).nf || (item as any).invoiceParam || 'Visualizar NF'}
      </CellLink>
    )
  }
  return <span>{(item as any).nf || (item as any).invoiceParam || '-'}</span>
}

const renderColorsCell = (resource: Resource) => {
  const colors = resource.colors || []
  if (Array.isArray(colors) && colors.length > 0) {
    return (
      <ColorListCell>
        {colors.map((c, ci) => (
          <ColorSwatchContainer key={ci}>
            <ColorSwatchCircle $hex={c.pantone?.hex || c.hex} />
            <span>
              {c.name}
              {c.pantone?.code ? ` (${c.pantone.code})` : ''}
            </span>
          </ColorSwatchContainer>
        ))}
      </ColorListCell>
    )
  }
  return <>{(resource as any).color || '-'}</>
}

const getFirstWithEllipsis = (content: string) => {
  if (!content) return '-'
  const parts = content
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length === 0) return '-'
  if (parts.length === 1) return parts[0]
  return `${parts[0]}...`
}

const formatNumericValue = (val: any) => {
  if (val === null || val === undefined || val === '') return '-'
  const num = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'))
  if (isNaN(num)) return String(val)
  return num.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

const getResourceQuantities = (resource: Resource) => {
  if (Array.isArray(resource.quantities) && resource.quantities.length > 0) {
    return resource.quantities.filter((q: any) => q.quantity != null && Number(q.quantity) > 0)
  }
  const list: any[] = []
  const sUnit = String(resource.stockUnit || '').toUpperCase().trim()

  const isLiters = ['LITROS', 'LITRO', 'L', 'LT', 'LTS'].includes(sUnit)
  const isPercent = ['%', 'PORCENTAGEM', 'PERCENT', 'PERCENTUAL'].includes(sUnit)
  const isM2 = ['M²', 'M2', 'METRO QUADRADO'].includes(sUnit)
  const isMLinear = ['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'].includes(sUnit)

  if (isLiters) {
    if (resource.totalWeight && Number(resource.totalWeight) > 0) {
      list.push({ quantity: resource.totalWeight, UnitOfMeasurement: { name: 'Litros' } })
    }
  } else if (isPercent) {
    if (resource.totalWeight && Number(resource.totalWeight) > 0) {
      list.push({ quantity: resource.totalWeight, UnitOfMeasurement: { name: '%' } })
    }
  } else if (isM2) {
    if (resource.totalWeight && Number(resource.totalWeight) > 0) {
      list.push({ quantity: resource.totalWeight, UnitOfMeasurement: { name: 'M²' } })
    }
  } else {
    if (resource.totalWeight && Number(resource.totalWeight) > 0) {
      list.push({ quantity: resource.totalWeight, UnitOfMeasurement: { name: 'KG' } })
    }
  }

  if (isMLinear) {
    if (resource.lengthInMm && Number(resource.lengthInMm) > 0) {
      list.push({ quantity: resource.lengthInMm, UnitOfMeasurement: { name: 'M linear' } })
    }
  } else {
    if (resource.lengthInMm && Number(resource.lengthInMm) > 0) {
      list.push({ quantity: resource.lengthInMm, UnitOfMeasurement: { name: 'MM' } })
    }
  }

  if (resource.unitQuantity && Number(resource.unitQuantity) > 0) {
    list.push({ quantity: resource.unitQuantity, UnitOfMeasurement: { name: 'UN' } })
  }
  return list
}

const getQuantityUnitName = (qty: any) =>
  (qty.UnitOfMeasurement?.name || qty.unitOfMeasurement?.name || qty.unit || 'N/A').toUpperCase()

const formatQuantityEntry = (qty: any) =>
  `${formatNumericValue(qty.quantity)} ${getQuantityUnitName(qty)}`

const getQuantityCellDisplay = (quantities?: any[]) => {
  if (!quantities || quantities.length === 0) return '-'
  const preferred = quantities.find((qty) => getQuantityUnitName(qty) === 'MM') ?? quantities[0]
  const formatted = formatQuantityEntry(preferred)
  return quantities.length > 1 ? `${formatted}...` : formatted
}

const formatQuantities = (quantities?: any[]) => {
  if (!quantities || quantities.length === 0) return '-'
  return quantities.map(formatQuantityEntry).join('\n')
}

const formatUnitPrice = (entry: any) => {
  if (!entry || entry.price === null || entry.price === undefined || entry.price === '') return '-'
  const num = typeof entry.price === 'number' ? entry.price : parseFloat(String(entry.price).replace(',', '.'))
  if (isNaN(num)) return String(entry.price)
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num)
}

const formatPrices = (prices?: PriceItem[]) => {
  if (!prices || prices.length === 0) return '-'
  return prices.map(price => {
    const formattedPrice = formatUnitPrice(price)
    return `${formattedPrice}/${getQuantityUnitName(price)}`
  }).join('\n')
}

const findQuantityEntryByUnit = (entries: any[] | undefined, unitKey: string) => {
  if (!entries || entries.length === 0) return undefined
  const unit = resourceQuantityColumns.find((u) => u.key === unitKey)
  if (!unit) return undefined
  return entries.find((entry) => unit.names.includes(getQuantityUnitName(entry)))
}

const findPriceEntryByUnit = (entries: any[] | undefined, unitKey: string) => {
  if (!entries || entries.length === 0) return undefined
  const unit = resourcePriceColumns.find((u) => u.key === unitKey)
  if (!unit) return undefined
  return entries.find((entry) => unit.names.includes(getQuantityUnitName(entry)))
}

const parseResourceNumber = (value: unknown): number | '' => {
  if (value == null || value === '') return ''
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'))
  return isNaN(num) ? '' : num
}

const numericResourceFields = new Set([
  'markup',
  'minQuantity',
  'maxQuantity',
  'dimension1',
  'dimension2',
])

const steelResourceColumnDefs: ResourceColumnDef[] = [
  { id: 'isActivated', label: 'Status', visible: true },
  { id: 'name', label: 'Insumo', visible: true },
  { id: 'code', label: 'Código', visible: true },
  { id: 'category', label: 'Categoria', visible: true },
  { id: 'type', label: 'Tipo', visible: true },
  { id: 'color', label: 'Cor', visible: false },
  { id: 'quantities', label: 'Conjunto de Quantidades', visible: true },
  { id: 'prices', label: 'Conjunto de Preço', visible: true },
  { id: 'markup', label: 'Markup', visible: false },
  { id: 'minQuantity', label: 'Estoque Mínimo', visible: false },
  { id: 'maxQuantity', label: 'Estoque Máximo', visible: false },
  { id: 'suppliers', label: 'Fornecedor', visible: false },
  { id: 'description', label: 'Descrição', visible: false },
  { id: 'dimension1', label: 'Dimensão 1', visible: false },
  { id: 'dimension2', label: 'Dimensão 2', visible: false },
  { id: 'material', label: 'Material', visible: false },
  { id: 'format', label: 'Formato', visible: false },
  { id: 'finishing', label: 'Acabamento', visible: false },
  { id: 'fabrication', label: 'Fabricação', visible: false },
  { id: 'ncm', label: 'NCM', visible: false },
  { id: 'shelf', label: 'Prateleira', visible: false },
  { id: 'stockLocation', label: 'Localização', visible: false },
  { id: 'stockStreet', label: 'Rua do Estoque', visible: false },
  { id: 'carrierCode', label: 'Código da transportadora', visible: false },
  { id: 'invoice', label: 'Nota Fiscal', visible: false },
]

const fashionResourceColumnDefs: ResourceColumnDef[] = [
  { id: 'isActivated', label: 'Status', visible: true },
  { id: 'name', label: 'Aviamento', visible: true },
  { id: 'code', label: 'Código', visible: true },
  { id: 'category', label: 'Categoria', visible: true },
  { id: 'type', label: 'Tipo', visible: true },
  { id: 'color', label: 'Cor', visible: true },
  { id: 'quantities', label: 'Conjunto de Quantidades', visible: true },
  { id: 'prices', label: 'Conjunto de Preço', visible: true },
  { id: 'markup', label: 'Markup', visible: false },
  { id: 'minQuantity', label: 'Estoque Mínimo', visible: false },
  { id: 'maxQuantity', label: 'Estoque Máximo', visible: false },
  { id: 'suppliers', label: 'Fornecedor', visible: false },
  { id: 'description', label: 'Descrição', visible: false },
  { id: 'dimension1', label: 'Dimensão 1', visible: false },
  { id: 'dimension2', label: 'Dimensão 2', visible: false },
  { id: 'material', label: 'Material', visible: false },
  { id: 'format', label: 'Formato', visible: false },
  { id: 'finishing', label: 'Acabamento', visible: false },
  { id: 'fabrication', label: 'Fabricação', visible: false },
  { id: 'ncm', label: 'NCM', visible: false },
  { id: 'shelf', label: 'Prateleira', visible: false },
  { id: 'stockLocation', label: 'Localização', visible: false },
  { id: 'stockStreet', label: 'Rua do Estoque', visible: false },
  { id: 'carrierCode', label: 'Código da transportadora', visible: false },
  { id: 'invoice', label: 'Nota Fiscal', visible: false },
]

const columnBehavior = (id: string): Partial<StandardColumn<Resource>> => {
  if (id === 'invoice') {
    return {
      sortable: false,
      accessor: (r) => r.externalInvoiceUrl || (r as any).nf || '',
      cell: (r) => <InvoiceCell item={r} />,
      exportValue: (r) => r.externalInvoiceUrl || (r as any).nf || '',
    }
  }
  if (id === 'isActivated') {
    return {
      accessor: (r) => r.isActivated,
      cell: (r) => {
        const isInactive = r.isActivated === false || (r as any).isActivated === 'false' || (r as any).isActivated === 0
        return <span>{isInactive ? 'Inativo' : 'Ativo'}</span>
      },
      exportValue: (r) => {
        const isInactive = r.isActivated === false || (r as any).isActivated === 'false' || (r as any).isActivated === 0
        return isInactive ? 'Inativo' : 'Ativo'
      },
    }
  }
  if (id === 'name') {
    return {
      accessor: (r) => r.name ?? '',
      cell: (r) => <OverflowCell text={r.name || '-'} />,
      exportValue: (r) => r.name ?? '',
    }
  }
  if (id === 'code') {
    return {
      accessor: (r) => r.code ?? '',
      cell: (r) => <OverflowCell text={r.code || '-'} />,
      exportValue: (r) => r.code ?? '',
    }
  }
  if (id === 'category') {
    return {
      accessor: (r) => r.category?.name ?? '',
      cell: (r) => <OverflowCell text={r.category?.name || '-'} />,
      exportValue: (r) => r.category?.name ?? '',
    }
  }
  if (id === 'type') {
    return {
      accessor: (r) => r.type?.name ?? '',
      cell: (r) => <OverflowCell text={r.type?.name || '-'} />,
      exportValue: (r) => r.type?.name ?? '',
    }
  }
  if (id === 'quantities') {
    return {
      excludeFromExport: true,
      sortable: false,
      accessor: (r) => getQuantityCellDisplay(getResourceQuantities(r)),
      cell: (r) => {
        const qList = getResourceQuantities(r)
        return (
          <OverflowCell
            text={formatQuantities(qList)}
            forceTooltip={qList.length > 1}
          >
            {getQuantityCellDisplay(qList)}
          </OverflowCell>
        )
      },
      exportValue: (r) => formatQuantities(getResourceQuantities(r)),
    }
  }
  if (id.startsWith('quantities_')) {
    const unitKey = id.slice('quantities_'.length)
    return {
      sortable: true,
      accessor: (r) => {
        const qList = getResourceQuantities(r)
        const entry = findQuantityEntryByUnit(qList, unitKey)
        if (!entry || entry.quantity === null || entry.quantity === undefined || entry.quantity === '') return -Infinity
        const val = typeof entry.quantity === 'number' ? entry.quantity : parseFloat(String(entry.quantity).replace(',', '.'))
        return isNaN(val) ? -Infinity : val
      },
      cell: (r) => {
        const qList = getResourceQuantities(r)
        const entry = findQuantityEntryByUnit(qList, unitKey)
        return <>{entry ? formatNumericValue(entry.quantity) : '-'}</>
      },
      exportValue: (r) => {
        const qList = getResourceQuantities(r)
        const entry = findQuantityEntryByUnit(qList, unitKey)
        return entry ? parseResourceNumber(entry.quantity) : ''
      },
    }
  }
  if (id === 'prices') {
    return {
      excludeFromExport: true,
      sortable: false,
      accessor: (r) => getFirstWithEllipsis(formatPrices(r.prices)),
      cell: (r) => {
        const pList = r.prices || []
        return (
          <OverflowCell
            text={formatPrices(pList)}
            forceTooltip={pList.length > 1}
          >
            {getFirstWithEllipsis(formatPrices(pList))}
          </OverflowCell>
        )
      },
      exportValue: (r) => formatPrices(r.prices),
    }
  }
  if (id.startsWith('prices_')) {
    const unitKey = id.slice('prices_'.length)
    return {
      sortable: true,
      accessor: (r) => {
        const entry = findPriceEntryByUnit(r.prices, unitKey)
        if (!entry || entry.price === null || entry.price === undefined || entry.price === '') return -Infinity
        const val = typeof entry.price === 'number' ? entry.price : parseFloat(String(entry.price).replace(',', '.'))
        return isNaN(val) ? -Infinity : val
      },
      cell: (r) => {
        const entry = findPriceEntryByUnit(r.prices, unitKey)
        return <>{entry ? formatUnitPrice(entry) : '-'}</>
      },
      exportValue: (r) => {
        const entry = findPriceEntryByUnit(r.prices, unitKey)
        return entry ? parseResourceNumber(entry.price) : ''
      },
    }
  }
  if (id === 'color') {
    return {
      sortable: false,
      accessor: (r) => (r.colors || []).map((c) => c.name).join(', ') || (r as any).color || '',
      cell: (r) => renderColorsCell(r),
      exportValue: (r) => (r.colors || []).map((c) => c.name).join(', ') || (r as any).color || '',
    }
  }
  if (id === 'suppliers') {
    return {
      accessor: (r) => r.suppliers?.map((s) => s.companyName).join(', ') ?? '',
      cell: (r) => {
        const sup = r.suppliers?.map((s) => s.companyName).join(', ')
        return <OverflowCell text={sup || '-'} />
      },
      exportValue: (r) => r.suppliers?.map((s) => s.companyName).join(', ') ?? '',
    }
  }
  if (numericResourceFields.has(id)) {
    return {
      accessor: (r) => (r as any)[id],
      cell: (r) => <>{(r as any)[id] != null ? formatNumericValue((r as any)[id]) : '-'}</>,
      exportValue: (r) => parseResourceNumber((r as any)[id]),
    }
  }
  return {
    accessor: (r) => (r as any)[id] ?? '',
    cell: (r) => {
      const raw = (r as any)[id]
      if (!raw) return <span>-</span>
      return <OverflowCell text={String(raw)} />
    },
    exportValue: (r) => {
      const raw = (r as any)[id]
      return raw ? String(raw) : ''
    },
  }
}

export const buildResourceColumns = (isFashion: boolean): StandardColumn<Resource>[] =>
  withUnitSubColumns(isFashion ? fashionResourceColumnDefs : steelResourceColumnDefs).map((def) => ({
    id: def.id,
    label: def.label,
    parentId: def.parentId,
    defaultHidden: !def.visible,
    ...columnBehavior(def.id),
  }))
